<script lang="ts" setup>
import { computed } from 'vue';

import { RemoveHotelSelectionFiltersOrigin } from '@apps/main/new-desktop/core/models';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { AppCheckbox } from '@libs/ui';
import type { AppMultiSelectOption } from '@libs/ui';
import type { BoardBasisCode } from '@libs/trip-planner-interfaces';

import type { HotelFilterMealsUpdate } from '../../../hotelFilters';
import { HotelMealFilters } from '../../../hotelFilters';

const { t } = i18n.global;

interface HotelStayFiltersProps {
  hasSelectedDateMode?: boolean;
  options?: AppMultiSelectOption<BoardBasisCode>[];
  hasFreeCancellationFilter?: boolean;
}

interface HotelStayFiltersEmits {
  (
    eventName: 'meal-option-selected',
    context: {
      filter: HotelFilterMealsUpdate;
      source: RemoveHotelSelectionFiltersOrigin;
    }
  ): void;
  (eventName: 'toggle-free-cancellation-filter', isChecked: boolean): void;
}

const emit = defineEmits<HotelStayFiltersEmits>();

const props = defineProps<HotelStayFiltersProps>();

const selectedPlaceholder = computed<string>(() => {
  const selectedOptionsAmount =
    props.options?.filter((option) => option.selected).length ?? 0;

  return t('planner.meal_plan_selected', selectedOptionsAmount);
});

function onSelectOption(selectedOption: HotelFilterMealsUpdate): void {
  emit('meal-option-selected', {
    filter: selectedOption,
    source: RemoveHotelSelectionFiltersOrigin.HotelDetails,
  });
}

function toggleFreeCancellationFilter(isChecked: boolean): void {
  emit('toggle-free-cancellation-filter', isChecked);
}
</script>

<template>
  <div class="hotel-stay-filters">
    <div class="hotel-stay-filters-wrapper">
      <HotelMealFilters
        v-if="props.options"
        type="modern-light"
        :use-teleport="false"
        :options="props.options"
        :selected-placeholder="selectedPlaceholder"
        :should-show-clear-button="false"
        selected-placeholder-format="x-selected"
        @meal-option-selected="onSelectOption($event)"
      />
      <!-- Only for selected date mode -->
      <div
        v-if="props.hasSelectedDateMode"
        class="hotel-stay-filters-free-cancellation"
      >
        <AppCheckbox
          :checked="props.hasFreeCancellationFilter"
          theme="grey"
          size="small"
          @change="toggleFreeCancellationFilter($event)"
        >
          {{ t('filter.rules.free_cancellation') }}
        </AppCheckbox>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.hotel-stay-filters {
  padding: var(--spacing-32) 0;
  border-top: 1px solid var(--color-neutral-30);

  .hotel-stay-filters-wrapper {
    display: flex;
    flex-direction: row;
    gap: var(--spacing-24);
    align-items: center;

    @include for-less-or-equal-than-599-px {
      flex-direction: column;
      gap: var(--spacing-16);
      align-items: flex-start;
    }
  }

  @include for-less-or-equal-than-1200-px {
    padding-left: var(--spacing-16);
    padding-right: var(--spacing-16);
  }
}
</style>
